import { type FC, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames/bind';
import {
  useSearchInputContext, type SearchRefs,
} from 'src/common/components/Search/SearchInputProvider';
import useIntersectionObserver from 'src/common/hooks/useIntersectionObserver';
import Button from 'src/common/components/ButtonDS22';
import { useSearchHeroModalContext } from 'src/common/components/Search/SearchHero/SearchHeroModalProvider';
import { useSearchHeroVisibilityContext } from 'src/common/components/Search/SearchHeroVisibilityProvider';
import type { SearchHeroTheme, SearchHeroVariant } from 'src/common/components/Search/types';
import PopoverDatesInput from 'src/common/components/Search/PopoverInputs/PopoverDatesInput';
import PopoverGuestsInput from 'src/common/components/Search/PopoverInputs/PopoverGuestsInput';
import PopoverLocationInput from 'src/common/components/Search/PopoverInputs/PopoverLocationInput';
import styles from './SearchHeroLayout.module.scss';

const cx = classNames.bind(styles);

export type SearchHeroInputsProps = {
  refs: SearchRefs;
  variant?: SearchHeroVariant;
  theme?: SearchHeroTheme;
};
const SearchHeroLayout: FC<SearchHeroInputsProps> = ({ variant, theme, refs }) => {
  const {
    isSearching, submitSearch,
  } = useSearchInputContext();
  const { setHeroInView } = useSearchHeroVisibilityContext();
  const modal = useSearchHeroModalContext();
  const rootRef = useRef<HTMLDivElement>(null);

  useIntersectionObserver<HTMLDivElement>({
    threshold: variant === 'hero' ? 1 : 0,
    ref: rootRef,
    callback: ([entry]) => {
      if (variant === 'hero') {
        // Notifies provider if in view. Used by Navigation to hide/show SearchNav
        setHeroInView(entry.isIntersecting);
      }

      // Moving search out of view, no longer needed so close any open modal
      if (!entry.isIntersecting) {
        modal.close();
      }
    },
  });

  const { t } = useTranslation('common');
  return (
    <div
      ref={rootRef}
      className={cx(styles.root, variant, {
        [`theme-${theme}`]: true,
      })}
      data-cwv={`search--${variant}`}
    >
      <div className={cx(styles.inputs, modal.active, variant)} ref={refs.inputs}>
        <PopoverLocationInput
          refs={refs}
          variant={variant}
          className={styles.location}
          rootRef={rootRef}
        />
        <div className={styles.divider} />
        <PopoverDatesInput refs={refs} variant={variant} className={cx(styles.dates, variant)} />
        <div className={styles.divider} />
        <PopoverGuestsInput refs={refs} variant={variant} className={cx(styles.guests, variant)} />
      </div>
      {/* Button props [size, rounded] vary between breakpoints */}
      {variant === 'hero' && (
        <div className={styles.buttonMobile}>
          <Button
            icon="magnifying-glass"
            onClick={() => submitSearch()}
            loading={isSearching}
            buttonRef={refs.button}
            title={t('search--search-action')}
            data-test="searchButton"
            size="medium"
            rounded
          />
        </div>
      )}
      <div className={styles.buttonDesktop}>
        <Button
          icon="magnifying-glass"
          onClick={() => submitSearch()}
          loading={isSearching}
          buttonRef={refs.button}
          title={t('search--search-action')}
          data-test="searchButton"
          size={variant === 'navigation' ? 'small' : 'large'}
          rounded={variant === 'navigation'}
        />
      </div>
    </div>
  );
};

export default SearchHeroLayout;
